import { useEffect, useState } from 'react';
import { Box, FormControl, MenuItem, Select, SelectChangeEvent, Theme, Typography } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { makeStyles } from 'tss-react/mui';

import {
  AuthType,
  DEFAULT_ONBOARDING_OPTION,
  ONBOARDING_OPTION_DEFINITIONS,
  normalizeOnboardingOptionValue,
} from './onboardingConfiguration';
import { getOnboardingDefaultConfig } from '../../api/clientsApi/clientsApi';

type DeliveryChannel = 'email' | 'phone' | 'both';
type OnboardingConfigurationFieldValue =
  | string
  | {
      authType?: string | null;
      linkDeliveryChannel?: string | null;
      otpChannel?: string | null;
    }
  | null
  | undefined;

const useStyles = makeStyles()((theme: Theme) => ({
  sectionWrapper: {
    width: '100%',
  },
  sectionWrapperHalf: {
    width: '50%',

    '@media (max-width: 1000px)': {
      width: '100%',
    },
  },
  cardContainer: {
    marginTop: '8px',
    padding: '16px',
    borderRadius: '12px',
    border: '1px solid rgba(255, 255, 255, 0.12)',
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  cardTitle: {
    fontFamily: 'Inter',
    fontWeight: 600,
    fontSize: '18px',
    lineHeight: '24px',
    color: theme.palette.common.white,
  },
  infoIcon: {
    color: theme.palette.info.light,
    fontSize: '18px',
    cursor: 'pointer',
  },
  cardSubtext: {
    marginTop: '6px',
    fontFamily: 'Inter',
    fontWeight: 400,
    fontSize: '13px',
    lineHeight: '18px',
    color: theme.palette.text.disabled,
  },
  fieldSection: {
    marginTop: '16px',
  },
  fieldTitle: {
    fontFamily: 'Inter',
    fontWeight: 500,
    fontSize: '13px',
    lineHeight: '18px',
    color: 'rgb(235, 230, 230)',
  },
  helperText: {
    marginTop: '6px',
    fontFamily: 'Inter',
    fontWeight: 400,
    fontSize: '11px',
    lineHeight: '16px',
    color: theme.palette.text.disabled,
  },
  helperNotice: {
    marginTop: '8px',
    fontFamily: 'Inter',
    fontWeight: 500,
    fontSize: '12px',
    lineHeight: '16px',
    color: theme.palette.warning.light,
  },
  segmentedWrapper: {
    marginTop: '8px',
    display: 'flex',
    gap: '6px',
    padding: '4px',
    borderRadius: '10px',
    backgroundColor: 'rgba(255, 255, 255, 0.06)',
  },
  segmentButton: {
    flex: 1,
    border: 'none',
    borderRadius: '8px',
    padding: '7px 10px',
    backgroundColor: 'transparent',
    color: 'rgb(235, 230, 230)',
    fontFamily: 'Inter',
    fontWeight: 500,
    fontSize: '13px',
    lineHeight: '18px',
    cursor: 'pointer',
    transition: 'none',
    minHeight: '34px',
  },
  segmentButtonActive: {
    backgroundColor: theme.palette.info.main,
    color: theme.palette.common.white,
  },
  segmentButtonDisabled: {
    opacity: 0.45,
    cursor: 'not-allowed',
  },
  selectContainer: {
    marginTop: '8px',
  },
  authSelect: {
    width: '100%',
    borderRadius: '10px',
    color: theme.palette.common.white,
    fontFamily: 'Inter',
    fontSize: '13px',
    backgroundColor: 'rgba(255, 255, 255, 0.02)',
    '.MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.18)',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.18)',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.info.main,
    },
    '.MuiSvgIcon-root': {
      color: theme.palette.common.white,
    },
  },
  hiddenField: {
    display: 'none',
  },
  menuPaper: {
    backgroundColor: '#1f2430',
    color: theme.palette.common.white,
  },
  menuItem: {
    fontFamily: 'Inter',
    fontSize: '13px',
  },
}));

type OnboardingConfigurationCardProps = {
  disabled?: boolean;
  firmConfigValue?: string | null;
  widthMode?: 'full' | 'half';
};

const OnboardingConfigurationCard = ({
  disabled = false,
  firmConfigValue,
  widthMode = 'full',
}: OnboardingConfigurationCardProps) => {
  const styles = useStyles();
  const { control, setValue, watch } = useFormContext();
  const [authTypeValue, setAuthTypeValue] = useState<string>(firmConfigValue || '');
  const areControlsDisabled = true;
  const onboardingOptionFromForm = watch('onboardingOption') as string | undefined;
  const onboardingConfigurationFromForm =
    watch('onboardingConfiguration') as OnboardingConfigurationFieldValue;
  const onboardingLinkDeliveryChannelFromForm =
    watch('onboardingLinkDeliveryChannel') as string | null | undefined;
  const onboardingOtpChannelFromForm =
    watch('onboardingOtpChannel') as string | null | undefined;
  const onboardingAuthTypeFromConfiguration =
    typeof onboardingConfigurationFromForm === 'string'
      ? onboardingConfigurationFromForm
      : onboardingConfigurationFromForm?.authType;
  const hasLoadedClientOnboarding =
    typeof onboardingConfigurationFromForm === 'object' &&
    onboardingConfigurationFromForm !== null &&
    !!onboardingConfigurationFromForm?.authType;
  const selectedOption = normalizeOnboardingOptionValue(
    onboardingOptionFromForm || onboardingAuthTypeFromConfiguration,
  );
  const phoneValue = (watch('phone') as string | undefined)?.trim() || '';
  const hasValidPhone = /^\+1\d{10}$/.test(phoneValue);
  const defaultOnboardingOption = 'emailOtpOnly';

  const normalizeChannelValue = (channelValue?: string | null): DeliveryChannel => {
    if (!channelValue) return 'email';

    const normalizedValue = channelValue.trim().toLowerCase();

    if (normalizedValue === 'both') return 'both';
    if (normalizedValue === 'phone' || normalizedValue === 'sms' || normalizedValue === 'text') return 'phone';

    return 'email';
  };

  const [linkChannel, setLinkChannel] = useState<DeliveryChannel>(
    normalizeChannelValue(
      typeof onboardingConfigurationFromForm === 'object'
        ? onboardingConfigurationFromForm?.linkDeliveryChannel
        : null,
    ),
  );
  const [otpChannel, setOtpChannel] = useState<DeliveryChannel>(
    normalizeChannelValue(
      typeof onboardingConfigurationFromForm === 'object'
        ? onboardingConfigurationFromForm?.otpChannel
        : null,
    ),
  );
  const [authType, setAuthType] = useState<AuthType>(
    normalizeOnboardingOptionValue(onboardingAuthTypeFromConfiguration || defaultOnboardingOption),
  );

  const getAuthTypeTitle = (authType?: string | null) => {
    if (!authType) return '';

    const normalizedAuthType = normalizeOnboardingOptionValue(authType);
    const matchedOption = ONBOARDING_OPTION_DEFINITIONS.find(
      (optionDefinition) => optionDefinition.value === normalizedAuthType,
    );

    return matchedOption?.title || '';
  };

  const handleLinkChannelChange = (channel: DeliveryChannel) => {
    if (disabled) return;
    if ((channel === 'phone' || channel === 'both') && !hasValidPhone) return;

    setLinkChannel(channel);
  };

  const handleOtpChannelChange = (channel: DeliveryChannel) => {
    if (disabled) return;
    if ((channel === 'phone' || channel === 'both') && !hasValidPhone) return;

    setOtpChannel(channel);
  };

  const handleAuthTypeChange = (event: SelectChangeEvent) => {
    if (disabled) return;

    const selectedAuthType = event.target.value as AuthType;
    const selectedOptionMeta = ONBOARDING_OPTION_DEFINITIONS.find(
      (optionDefinition) => optionDefinition.value === selectedAuthType,
    );

    if (!hasValidPhone && selectedOptionMeta?.requiresValidPhone) {
      return;
    }

    setAuthType(selectedAuthType);
    setValue('onboardingOption', selectedAuthType, { shouldDirty: true });
  };

  useEffect(() => {
    const selectedOptionMeta = ONBOARDING_OPTION_DEFINITIONS.find(
      (optionDefinition) => optionDefinition.value === selectedOption,
    );

    if (selectedOptionMeta?.requiresValidPhone && !hasValidPhone) {
      setValue('onboardingOption', DEFAULT_ONBOARDING_OPTION, { shouldDirty: true });
      setAuthType(DEFAULT_ONBOARDING_OPTION);
    }
  }, [hasValidPhone, selectedOption, setValue]);

  useEffect(() => {
    const onboardingConfig = normalizeOnboardingOptionValue(
      onboardingOptionFromForm || onboardingAuthTypeFromConfiguration || defaultOnboardingOption,
    );
    setAuthType(onboardingConfig);
    setValue('onboardingOption', onboardingConfig, { shouldDirty: false });
  }, [onboardingAuthTypeFromConfiguration, onboardingOptionFromForm, setValue]);

  useEffect(() => {
    let isMounted = true;

    getOnboardingDefaultConfig()
      .then((response) => {
        if (!isMounted) return;

        const defaultAuthType = normalizeOnboardingOptionValue(response.data?.authType || firmConfigValue);
        const selectedAuthType = hasLoadedClientOnboarding
          ? normalizeOnboardingOptionValue(onboardingAuthTypeFromConfiguration)
          : defaultAuthType;
        const selectedLinkChannelRaw = hasLoadedClientOnboarding
          ? onboardingConfigurationFromForm?.linkDeliveryChannel ?? null
          : (response.data?.linkDeliveryChannel ?? onboardingLinkDeliveryChannelFromForm ?? null);
        const selectedOtpChannelRaw = hasLoadedClientOnboarding
          ? onboardingConfigurationFromForm?.otpChannel ?? null
          : (response.data?.otpChannel ?? onboardingOtpChannelFromForm ?? null);
        setAuthTypeValue(getAuthTypeTitle(defaultAuthType));
        setAuthType(selectedAuthType);
        setLinkChannel(normalizeChannelValue(selectedLinkChannelRaw));
        setOtpChannel(normalizeChannelValue(selectedOtpChannelRaw));
        setValue('onboardingOption', selectedAuthType, { shouldDirty: false });
        setValue('onboardingLinkDeliveryChannel', selectedLinkChannelRaw, {
          shouldDirty: false,
        });
        setValue('onboardingOtpChannel', selectedOtpChannelRaw, { shouldDirty: false });
      })
      .catch(() => {
        if (!isMounted) return;

        const fallbackAuthType = hasLoadedClientOnboarding
          ? normalizeOnboardingOptionValue(onboardingAuthTypeFromConfiguration)
          : normalizeOnboardingOptionValue(firmConfigValue);
        const fallbackLinkChannel =
          hasLoadedClientOnboarding
            ? onboardingConfigurationFromForm?.linkDeliveryChannel ?? null
            : (onboardingLinkDeliveryChannelFromForm ?? null);
        const fallbackOtpChannel =
          hasLoadedClientOnboarding
            ? onboardingConfigurationFromForm?.otpChannel ?? null
            : (onboardingOtpChannelFromForm ?? null);

        setAuthTypeValue(getAuthTypeTitle(fallbackAuthType));
        setAuthType(fallbackAuthType);
        setLinkChannel(normalizeChannelValue(fallbackLinkChannel));
        setOtpChannel(normalizeChannelValue(fallbackOtpChannel));
        setValue('onboardingOption', fallbackAuthType, { shouldDirty: false });
        setValue('onboardingLinkDeliveryChannel', fallbackLinkChannel, { shouldDirty: false });
        setValue('onboardingOtpChannel', fallbackOtpChannel, { shouldDirty: false });
      });

    return () => {
      isMounted = false;
    };
  }, [
    firmConfigValue,
    hasLoadedClientOnboarding,
    onboardingAuthTypeFromConfiguration,
    onboardingConfigurationFromForm,
    onboardingLinkDeliveryChannelFromForm,
    onboardingOtpChannelFromForm,
    setValue,
  ]);

  return (
    <Box
      className={`${styles.classes.sectionWrapper} ${widthMode === 'half' ? styles.classes.sectionWrapperHalf : ''
        }`}
    >
      <Box className={styles.classes.titleRow}>
        <Typography className={styles.classes.cardTitle}>Onboarding Configuration</Typography>
      </Box>

      <Box className={styles.classes.cardContainer}>
        <Typography className={styles.classes.cardSubtext}>Defaults applied from Firm configuration</Typography>

        {!hasValidPhone && (
          <Typography className={styles.classes.helperNotice}>
            Text OTP options are read-only until a valid mobile number is provided.
          </Typography>
        )}

        <Box className={styles.classes.fieldSection}>
          <Typography className={styles.classes.fieldTitle}>Link Delivery Channel</Typography>
          <Box className={styles.classes.segmentedWrapper}>
            {[
              { label: 'Email', value: 'email' as DeliveryChannel },
              { label: 'Phone', value: 'phone' as DeliveryChannel },
              { label: 'Both', value: 'both' as DeliveryChannel },
            ].map((option) => {
              const isDisabled = areControlsDisabled || disabled;

              return (
                <button
                  key={option.value}
                  type="button"
                  disabled={isDisabled}
                  onClick={() => handleLinkChannelChange(option.value)}
                  className={`${styles.classes.segmentButton} ${linkChannel === option.value ? styles.classes.segmentButtonActive : ''
                    } ${isDisabled ? styles.classes.segmentButtonDisabled : ''}`}
                >
                  {option.label}
                </button>
              );
            })}
          </Box>
          <Typography className={styles.classes.helperText}>
            Channel used to deliver the initial onboarding link.
          </Typography>
        </Box>

        <Box className={styles.classes.fieldSection}>
          <Typography className={styles.classes.fieldTitle}>Communication Channel</Typography>
          <Box className={styles.classes.segmentedWrapper}>
            {[
              { label: 'Email', value: 'email' as DeliveryChannel },
              { label: 'Phone', value: 'phone' as DeliveryChannel },
              { label: 'Both', value: 'both' as DeliveryChannel },
            ].map((option) => {
              const isDisabled = areControlsDisabled || disabled;

              return (
                <button
                  key={option.value}
                  type="button"
                  disabled={isDisabled}
                  onClick={() => handleOtpChannelChange(option.value)}
                  className={`${styles.classes.segmentButton} ${otpChannel === option.value ? styles.classes.segmentButtonActive : ''
                    } ${isDisabled ? styles.classes.segmentButtonDisabled : ''}`}
                >
                  {option.label}
                </button>
              );
            })}
          </Box>
          <Typography className={styles.classes.helperText}>Channel used to deliver notifications and OTP codes.</Typography>
        </Box>

        <Box className={styles.classes.fieldSection}>
          <Typography className={styles.classes.fieldTitle}>Authentication Type</Typography>
          <Typography className={styles.classes.helperText}>Authentication default: {authTypeValue || '—'}</Typography>

          <Box className={styles.classes.selectContainer}>
            <FormControl fullWidth>
              <Select
                value={authType}
                onChange={handleAuthTypeChange}
                // disabled={areControlsDisabled || disabled}s
                className={styles.classes.authSelect}
                MenuProps={{
                  classes: { paper: styles.classes.menuPaper },
                }}
              >
                {ONBOARDING_OPTION_DEFINITIONS.map((option) => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    className={styles.classes.menuItem}
                  // disabled={option.requiresValidPhone && !hasValidPhone}
                  >
                    {option.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Typography className={styles.classes.helperText}>
            Defines how customer will verify identity.
          </Typography>
        </Box>

        <Box className={styles.classes.hiddenField}>
          <Controller
            name="onboardingOption"
            control={control}
            render={({ field }) => <input type="hidden" {...field} value={authType} />}
          />
          <Controller
            name="onboardingLinkDeliveryChannel"
            control={control}
            render={({ field }) => (
              <input type="hidden" {...field} value={linkChannel || ''} />
            )}
          />
          <Controller
            name="onboardingOtpChannel"
            control={control}
            render={({ field }) => (
              <input type="hidden" {...field} value={otpChannel || ''} />
            )}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default OnboardingConfigurationCard;
